
import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const industries = [
  {
    id: 'retail',
    icon: 'ri-store-2-line',
    name: 'Retail & Restaurants',
    image: 'https://public.readdy.ai/ai/img_res/c9a1787eac512ac56116053eadc82e10.jpg',
    description: 'Reduce shrinkage, deter shoplifting, and protect staff with integrated camera and alarm systems tailored for retail environments.',
    challenges: ['Shoplifting & internal theft', 'After-hours break-ins', 'Cash handling security', 'Staff safety'],
    solutions: ['HD cameras at all entry points & POS', 'Motion-triggered alarm systems', 'Access control for back offices', '24/7 live monitoring'],
  },
  {
    id: 'office',
    icon: 'ri-building-2-line',
    name: 'Office Buildings',
    image: 'https://readdy.ai/api/search-image?query=modern%20corporate%20office%20building%20lobby%20with%20glass%20entrance%20doors%20security%20desk%20reception%20area%20professional%20business%20environment%20clean%20contemporary%20architecture%20natural%20lighting&width=800&height=600&seq=bizoffice1&orientation=landscape',
    description: 'Control who enters your building, protect sensitive data areas, and ensure employee safety with enterprise-grade access control.',
    challenges: ['Unauthorized building access', 'After-hours intrusions', 'Sensitive data area security', 'Visitor management'],
    solutions: ['Card & biometric access control', 'Lobby camera surveillance', 'Server room restricted access', 'Visitor check-in integration'],
  },
  {
    id: 'warehouse',
    icon: 'ri-building-4-line',
    name: 'Warehouses & Industrial',
    image: 'https://readdy.ai/api/search-image?query=large%20modern%20warehouse%20interior%20with%20high%20ceilings%20rows%20of%20shelving%20inventory%20storage%20industrial%20facility%20with%20bright%20overhead%20lighting%20professional%20logistics%20space%20clean%20organized&width=800&height=600&seq=bizwarehouse1&orientation=landscape',
    description: 'Protect high-value inventory, monitor loading docks, and secure large perimeters with scalable surveillance systems.',
    challenges: ['Inventory & cargo theft', 'Loading dock security', 'Large perimeter coverage', 'Equipment protection'],
    solutions: ['PTZ cameras for wide coverage', 'Loading dock monitoring', 'Perimeter alarm systems', 'License plate recognition'],
  },
  {
    id: 'healthcare',
    icon: 'ri-hospital-line',
    name: 'Healthcare & Medical',
    image: 'https://readdy.ai/api/search-image?query=modern%20medical%20clinic%20reception%20area%20clean%20white%20interior%20professional%20healthcare%20environment%20with%20reception%20desk%20waiting%20area%20bright%20natural%20lighting%20contemporary%20design&width=800&height=600&seq=bizhealthcare1&orientation=landscape',
    description: 'Meet compliance requirements, protect pharmaceuticals, and ensure patient and staff safety with HIPAA-aware security solutions.',
    challenges: ['Pharmaceutical security', 'Patient privacy compliance', 'Staff safety after hours', 'Restricted area access'],
    solutions: ['Biometric access for drug storage', 'Discreet dome cameras', 'Emergency lockdown capability', 'Detailed audit trail logging'],
  },
  {
    id: 'hospitality',
    icon: 'ri-hotel-line',
    name: 'Hotels & Hospitality',
    image: 'https://readdy.ai/api/search-image?query=luxury%20hotel%20lobby%20interior%20with%20elegant%20decor%20marble%20floors%20high%20ceilings%20warm%20lighting%20professional%20hospitality%20environment%20with%20reception%20desk%20and%20seating%20area%20upscale%20design&width=800&height=600&seq=bizhotel1&orientation=landscape',
    description: 'Protect guests, staff, and assets across your entire property with seamlessly integrated security that enhances the guest experience.',
    challenges: ['Guest & staff safety', 'Parking lot security', 'Asset protection', 'Multiple access zones'],
    solutions: ['Lobby & corridor surveillance', 'Parking lot cameras', 'Key card access control', 'Pool & amenity monitoring'],
  },
  {
    id: 'construction',
    icon: 'ri-tools-line',
    name: 'Construction Sites',
    image: 'https://readdy.ai/api/search-image?query=active%20construction%20site%20at%20dusk%20with%20cranes%20heavy%20equipment%20and%20building%20materials%20professional%20security%20surveillance%20perspective%20dramatic%20lighting%20industrial%20setting%20with%20safety%20barriers&width=800&height=600&seq=bizconstruct1&orientation=landscape',
    description: 'Prevent equipment theft, monitor site activity 24/7, and reduce insurance costs with live-monitored construction site security.',
    challenges: ['Equipment & material theft', 'After-hours vandalism', 'Unauthorized site access', 'Insurance requirements'],
    solutions: ['Solar-powered temporary cameras', '24/7 live video monitoring', 'Audio talk-down deterrence', 'Time-lapse documentation'],
  },
];

const solutions = [
  {
    icon: 'ri-camera-line',
    title: 'Commercial Camera Systems',
    description: 'Enterprise-grade 4K surveillance with AI detection, license plate recognition, and centralized management across multiple locations.',
    link: '/camera-packages',
    features: ['4K Ultra HD resolution', 'AI person & vehicle detection', 'Multi-site management', 'Cloud + local storage'],
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Business Alarm Systems',
    description: 'Commercial-grade alarm systems with cellular backup, silent panic buttons, and instant dispatch to local authorities.',
    link: '/alarm-packages',
    features: ['Cellular & IP communication', 'Silent panic buttons', 'Perimeter protection', 'Smart home integration'],
  },
  {
    icon: 'ri-key-2-line',
    title: 'Access Control Systems',
    description: 'Control who enters every door, every zone, at any time. Card, fob, biometric, and mobile credential options available.',
    link: '/access-control',
    features: ['Card, fob & biometric options', 'Time-based access rules', 'Detailed audit logs', 'Remote management'],
  },
  {
    icon: 'ri-live-line',
    title: 'Live Video Monitoring',
    description: 'Trained security professionals watch your cameras in real-time, 24/7 — deterring threats before they escalate.',
    link: '/live-monitoring',
    features: ['24/7 trained operators', 'Audio talk-down capability', 'Instant police dispatch', '50–70% cheaper than guards'],
  },
];

const stats = [
  { value: '500+', label: 'Commercial Clients', icon: 'ri-building-2-line' },
  { value: '25+', label: 'Years in Business Security', icon: 'ri-award-line' },
  { value: '24/7', label: 'Monitoring & Support', icon: 'ri-time-line' },
  { value: 'A+', label: 'BBB Rating', icon: 'ri-shield-star-line' },
];

const faqs = [
  {
    question: 'What is the best security system for a small business?',
    answer: 'For most small businesses, we recommend a combination of HD security cameras at entry points and POS areas, a monitored alarm system with cellular backup, and basic access control for back offices or server rooms. A 4-camera system with a monitored alarm typically starts around $1,500–$3,000 installed, with monitoring from $29.99/month. We offer free site assessments to design the right solution for your specific layout and budget.',
  },
  {
    question: 'How much does commercial security cost in the GTA?',
    answer: 'Commercial security costs vary based on property size, number of cameras, and monitoring level. Typical ranges: Small retail/office (4–8 cameras + alarm): $2,500–$6,000 installed. Mid-size commercial (8–16 cameras + access control): $6,000–$15,000. Large industrial/multi-site: $15,000+. Monthly monitoring ranges from $39.99 to $79.99 depending on services. We provide transparent, itemized quotes with no hidden fees.',
  },
  {
    question: 'Can I monitor multiple business locations from one system?',
    answer: 'Yes. Our enterprise camera and access control systems support centralized management of multiple locations through a single app or web portal. You can view live feeds, review footage, manage access credentials, and receive alerts from all your sites simultaneously — whether you have 2 locations or 20.',
  },
  {
    question: 'Will a business security system lower my insurance premiums?',
    answer: 'In most cases, yes. Many commercial insurance providers in Ontario offer discounts of 5–20% for businesses with professionally monitored alarm systems and verified camera coverage. Some insurers require ULC-listed monitoring stations. We can provide documentation of your system for insurance purposes. The savings often offset a significant portion of your monthly monitoring cost.',
  },
  {
    question: 'What is the difference between commercial and residential security systems?',
    answer: 'Commercial systems are designed for higher traffic, larger spaces, and stricter compliance requirements. Key differences include: more robust hardware rated for continuous use, multi-zone access control for different departments, higher camera counts with wider coverage, integration with business systems (POS, HR, time & attendance), and compliance-ready features like 90-day video retention for regulated industries.',
  },
  {
    question: 'Do you offer security systems for businesses with multiple locations?',
    answer: 'Absolutely. We specialize in multi-site commercial deployments across the GTA. We can standardize your security infrastructure across all locations, provide centralized monitoring and management, and offer volume pricing for businesses with 3 or more sites. Our team handles everything from site surveys to installation and ongoing support at every location.',
  },
  {
    question: 'How long does commercial security installation take?',
    answer: 'Installation time depends on the scope of the project. A typical small business (4–8 cameras + alarm) is completed in 1 day. Mid-size commercial installations (access control + 16 cameras) typically take 2–3 days. Large industrial or multi-floor projects may take 1–2 weeks. We schedule installations to minimize disruption to your business operations, including after-hours and weekend installs.',
  },
  {
    question: 'What happens if my business alarm goes off after hours?',
    answer: 'With our professional monitoring service, when your alarm triggers, our ULC-certified monitoring center is immediately notified. Operators attempt to contact your designated key holders. If they cannot reach anyone or you confirm an emergency, local police are dispatched within seconds. You also receive instant push notifications and can view live camera footage through our mobile app to assess the situation remotely.',
  },
];

const complianceItems = [
  { icon: 'ri-leaf-line', label: 'Cannabis Facilities', detail: '90-day video retention compliance' },
  { icon: 'ri-hospital-line', label: 'Healthcare (HIPAA)', detail: 'Audit trails & restricted access' },
  { icon: 'ri-bank-line', label: 'Financial Institutions', detail: 'High-security vault & ATM coverage' },
  { icon: 'ri-government-line', label: 'Government Buildings', detail: 'Multi-factor authentication' },
  { icon: 'ri-truck-line', label: 'Logistics & Transport', detail: 'Cargo & fleet monitoring' },
  { icon: 'ri-restaurant-line', label: 'Food & Beverage', label2: 'Health code compliance cameras' },
];

export default function BusinessSecurityPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [industryDropdownOpen, setIndustryDropdownOpen] = useState(false);
  const [activeIndustry, setActiveIndustry] = useState(industries[0].id);

  const industryOptions = [
    'Retail & Restaurants',
    'Office Buildings',
    'Warehouses & Industrial',
    'Healthcare & Medical',
    'Hotels & Hospitality',
    'Construction Sites',
    'Car Dealerships',
    'Cannabis Facilities',
    'Multi-Family Residential',
    'Other',
  ];

  const scrollToForm = () => {
    const el = document.getElementById('business-assessment-form');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    const form = e.currentTarget;
    const formData = new FormData(form);
    formData.set('industry', selectedIndustry);

    const textarea = form.querySelector('textarea');
    if (textarea && textarea.value.length > 500) {
      setFormStatus('error');
      return;
    }

    try {
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => urlEncoded.append(key, value.toString()));

      const response = await fetch('https://readdy.ai/api/form/d6gg9g6qpmcju3gsd8h0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setSelectedIndustry('');
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  const activeIndustryData = industries.find((i) => i.id === activeIndustry)!;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Commercial Business Security Systems',
    provider: {
      '@type': 'LocalBusiness',
      name: 'Alliance Security Systems',
      telephone: '1-888-458-9181',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '2355 Derry Road East, Unit 1',
        addressLocality: 'Mississauga',
        addressRegion: 'ON',
        postalCode: 'L5S 1V6',
        addressCountry: 'CA',
      },
    },
    areaServed: 'Greater Toronto Area',
    description: 'Professional commercial security systems including cameras, alarms, access control, and live monitoring for businesses across the GTA.',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Business Security Solutions',
      itemListElement: [
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Commercial Camera Systems' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Business Alarm Systems' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Access Control Systems' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Live Video Monitoring' } },
      ],
    },
  };

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Commercial Business Security Systems GTA | Cameras, Alarms & Access Control | Alliance Security"
        description="Professional business security systems for retail, offices, warehouses, healthcare & more across the GTA. HD cameras, monitored alarms, access control & 24/7 live monitoring. 500+ commercial clients. Free site assessment — call 1-888-458-9181."
        keywords="commercial security systems, business security GTA, office security cameras, retail security systems, warehouse security, access control Toronto, business alarm systems Mississauga"
        canonical="/business-security"
        structuredData={[structuredData, faqStructuredData]}
      />

      {/* Navigation */}
      <nav className="bg-white shadow-md sticky top-0 z-50" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex flex-col leading-none border-2 border-[#D4A853] rounded-lg px-3 py-2 bg-[#0A1628]">
              <span className="text-xl sm:text-2xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
              <span className="text-[8px] sm:text-[10px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
            </div>
          </Link>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer">
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
          <div className="hidden md:flex items-center gap-5">
            <Link to="/" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">Home</Link>
            <Link to="/camera-packages" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">Cameras</Link>
            <Link to="/alarm-packages" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">Alarms</Link>
            <Link to="/access-control" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">Access Control</Link>
            <Link to="/live-monitoring" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">Live Monitoring</Link>
            <Link to="/clients" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">Clients</Link>
            <Link to="/faq" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">FAQ</Link>
            <a href="tel:18884589181" className="flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-phone-line"></i>1-888-458-9181
            </a>
            <button onClick={scrollToForm} className="bg-[#D4A853] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap">
              Free Assessment
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 pb-4 space-y-1">
            <Link to="/" className="block py-2.5 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Home</Link>
            <Link to="/camera-packages" className="block py-2.5 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Cameras</Link>
            <Link to="/alarm-packages" className="block py-2.5 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Alarms</Link>
            <Link to="/access-control" className="block py-2.5 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Access Control</Link>
            <Link to="/live-monitoring" className="block py-2.5 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Live Monitoring</Link>
            <Link to="/clients" className="block py-2.5 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Clients</Link>
            <Link to="/faq" className="block py-2.5 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">FAQ</Link>
            <a href="tel:18884589181" className="flex items-center gap-2 py-2.5 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50"><i className="ri-phone-line"></i>1-888-458-9181</a>
            <button onClick={scrollToForm} className="w-full bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap mt-2">Free Assessment</button>
          </div>
        )}
      </nav>

      <main>
        {/* Hero */}
        <section className="relative min-h-[90vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=modern%20commercial%20business%20district%20at%20dusk%20with%20glass%20office%20towers%20retail%20storefronts%20and%20professional%20buildings%20warm%20golden%20hour%20lighting%20urban%20GTA%20skyline%20professional%20photography%20wide%20angle%20view&width=1920&height=1080&seq=bizhero1&orientation=landscape"
              alt="Commercial Business Security GTA"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/90 via-[#0A1628]/70 to-[#0A1628]/40"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 w-full py-16">
            <nav className="flex items-center gap-2 text-xs sm:text-sm text-white/60 mb-8" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-[#D4A853] cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-[#D4A853]">Business Security</span>
            </nav>

            <div className="max-w-3xl">
              <span className="inline-block bg-[#D4A853] text-white px-5 py-2 rounded-full text-sm font-semibold mb-5 whitespace-nowrap">
                COMMERCIAL SECURITY SPECIALISTS
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-5 leading-tight">
                Business Security Systems for the GTA
              </h1>
              <p className="text-lg sm:text-xl text-white/85 mb-8 leading-relaxed max-w-2xl">
                Protect your business with enterprise-grade cameras, monitored alarms, access control, and live video monitoring — all installed and supported by Alliance Security's certified team since 1999.
              </p>

              <div className="flex flex-wrap gap-3 mb-8">
                {['Retail', 'Offices', 'Warehouses', 'Healthcare', 'Hospitality', 'Construction'].map((tag) => (
                  <span key={tag} className="bg-white/10 border border-white/20 text-white/90 px-4 py-1.5 rounded-full text-xs font-medium backdrop-blur-sm">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={scrollToForm}
                  className="bg-[#D4A853] text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-lg flex items-center justify-center gap-3"
                >
                  Get Free Business Assessment
                  <span className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-arrow-right-line text-lg"></i>
                  </span>
                </button>
                <a
                  href="tel:18884589181"
                  className="border-2 border-white text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-white hover:text-[#0A1628] transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-phone-line"></i>
                  1-888-458-9181
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-[#0A1628] py-6 sm:py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
              {stats.map((s, i) => (
                <div key={i} className="text-center">
                  <div className="w-10 h-10 bg-[#D4A853]/20 rounded-full flex items-center justify-center mx-auto mb-2">
                    <i className={`${s.icon} text-lg text-[#D4A853]`}></i>
                  </div>
                  <p className="text-2xl sm:text-3xl font-bold text-[#D4A853]">{s.value}</p>
                  <p className="text-xs sm:text-sm text-white/70 mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Business Security Matters */}
        <section className="py-16 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                  WHY IT MATTERS
                </span>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-5 leading-tight">
                  The Real Cost of Inadequate Business Security
                </h2>
                <p className="text-base sm:text-lg text-gray-600 mb-6 leading-relaxed">
                  Canadian businesses lose over <strong>$5 billion annually</strong> to theft, vandalism, and break-ins. Without a professional security system, your business is significantly more vulnerable — and your insurance premiums reflect that risk.
                </p>
                <div className="space-y-4 mb-8">
                  {[
                    { icon: 'ri-alarm-warning-line', color: 'text-red-500', bg: 'bg-red-50', stat: '4x', text: 'Higher break-in risk for businesses without monitored alarms' },
                    { icon: 'ri-money-dollar-circle-line', color: 'text-orange-500', bg: 'bg-orange-50', stat: '$8,400', text: 'Average loss per commercial break-in in Canada' },
                    { icon: 'ri-user-unfollow-line', color: 'text-amber-600', bg: 'bg-amber-50', stat: '42%', text: 'Of business theft is committed by employees (internal theft)' },
                    { icon: 'ri-shield-check-line', color: 'text-emerald-600', bg: 'bg-emerald-50', stat: '20%', text: 'Average insurance discount with professionally monitored system' },
                  ].map((item, i) => (
                    <div key={i} className={`flex items-center gap-4 ${item.bg} rounded-xl p-4`}>
                      <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                        <i className={`${item.icon} text-2xl ${item.color}`}></i>
                      </div>
                      <div>
                        <span className="text-xl font-bold text-[#0A1628]">{item.stat}</span>
                        <span className="text-sm text-gray-600 ml-2">{item.text}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <button onClick={scrollToForm} className="bg-[#D4A853] text-white px-8 py-3.5 rounded-full font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-lg">
                  Protect My Business Now
                </button>
              </div>

              <div className="relative">
                <div className="w-full h-[350px] sm:h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://readdy.ai/api/search-image?query=professional%20security%20technician%20installing%20commercial%20security%20camera%20system%20on%20office%20building%20exterior%20daylight%20clear%20sky%20certified%20installer%20with%20equipment%20professional%20work%20environment&width=800&height=700&seq=bizinstall1&orientation=portrait"
                    alt="Commercial Security Installation"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-5 -left-5 bg-[#D4A853] rounded-2xl p-5 shadow-xl">
                  <p className="text-3xl font-bold text-white">30,000+</p>
                  <p className="text-sm text-white/80">Installations<br/>Since 1999</p>
                </div>
                <div className="absolute -top-5 -right-5 bg-white rounded-2xl p-4 shadow-xl border border-gray-100">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 bg-[#003DA5] rounded-lg flex items-center justify-center">
                      <span className="text-white font-extrabold text-sm">A+</span>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-[#0A1628]">BBB Accredited</p>
                      <p className="text-[10px] text-gray-500">Since 1999</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Business Security Solutions */}
        <section className="py-16 sm:py-24 bg-[#F8F9FA]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                COMPLETE SOLUTIONS
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
                Everything Your Business Needs
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Integrated commercial security systems designed to work together seamlessly
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
              {solutions.map((sol, i) => (
                <div key={i} className="bg-white rounded-2xl p-7 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-gray-100 group">
                  <div className="flex items-start gap-5 mb-5">
                    <div className="w-14 h-14 bg-[#D4A853] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <i className={`${sol.icon} text-2xl text-white`}></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#0A1628] mb-1">{sol.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{sol.description}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-5">
                    {sol.features.map((f, fi) => (
                      <div key={fi} className="flex items-center gap-2">
                        <i className="ri-check-line text-[#D4A853] flex-shrink-0"></i>
                        <span className="text-xs text-gray-700">{f}</span>
                      </div>
                    ))}
                  </div>
                  <Link
                    to={sol.link}
                    className="inline-flex items-center gap-2 text-[#D4A853] font-semibold text-sm hover:gap-3 transition-all cursor-pointer"
                  >
                    Learn More <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industry-Specific Solutions */}
        <section className="py-16 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-14">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                INDUSTRY EXPERTISE
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
                Security Solutions by Industry
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Every industry has unique security challenges. We design solutions that address yours specifically.
              </p>
            </div>

            {/* Industry Tabs */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-10">
              {industries.map((ind) => (
                <button
                  key={ind.id}
                  onClick={() => setActiveIndustry(ind.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeIndustry === ind.id
                      ? 'bg-[#D4A853] text-white shadow-md'
                      : 'bg-[#F8F9FA] text-gray-600 hover:bg-[#D4A853]/10 hover:text-[#D4A853] border border-gray-200'
                  }`}
                >
                  <i className={`${ind.icon} text-base`}></i>
                  {ind.name}
                </button>
              ))}
            </div>

            {/* Active Industry Detail */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div className="relative w-full h-[280px] sm:h-[420px] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={activeIndustryData.image}
                  alt={activeIndustryData.name}
                  className="w-full h-full object-cover object-center transition-all duration-500"
                />
                <div className="absolute top-4 left-4 bg-[#D4A853] text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 whitespace-nowrap">
                  <i className={`${activeIndustryData.icon} text-base`}></i>
                  {activeIndustryData.name}
                </div>
              </div>

              <div>
                <h3 className="text-2xl sm:text-3xl font-bold text-[#0A1628] mb-3">{activeIndustryData.name} Security</h3>
                <p className="text-base text-gray-600 mb-6 leading-relaxed">{activeIndustryData.description}</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-7">
                  <div className="bg-red-50 rounded-xl p-5">
                    <h4 className="text-sm font-bold text-red-800 mb-3 flex items-center gap-2">
                      <i className="ri-error-warning-line text-lg"></i>
                      Common Challenges
                    </h4>
                    <ul className="space-y-2">
                      {activeIndustryData.challenges.map((c, ci) => (
                        <li key={ci} className="flex items-start gap-2 text-sm text-red-700">
                          <i className="ri-close-circle-line text-red-500 mt-0.5 flex-shrink-0"></i>
                          {c}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-5">
                    <h4 className="text-sm font-bold text-emerald-800 mb-3 flex items-center gap-2">
                      <i className="ri-shield-check-line text-lg"></i>
                      Our Solutions
                    </h4>
                    <ul className="space-y-2">
                      {activeIndustryData.solutions.map((s, si) => (
                        <li key={si} className="flex items-start gap-2 text-sm text-emerald-700">
                          <i className="ri-check-line text-emerald-500 mt-0.5 flex-shrink-0"></i>
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <button
                  onClick={scrollToForm}
                  className="bg-[#0A1628] text-white px-7 py-3.5 rounded-full font-semibold hover:bg-[#D4A853] transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
                >
                  Get {activeIndustryData.name} Quote
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Compliance & Regulated Industries */}
        <section className="py-16 sm:py-20 bg-[#0A1628]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-14">
              <span className="inline-block bg-[#D4A853] text-white px-5 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                COMPLIANCE-READY
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Security for Regulated Industries
              </h2>
              <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto">
                We understand the compliance requirements of regulated industries and design systems that meet or exceed them
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-5 mb-12">
              {[
                { icon: 'ri-leaf-line', label: 'Cannabis', detail: '90-day retention' },
                { icon: 'ri-hospital-line', label: 'Healthcare', detail: 'HIPAA-aware' },
                { icon: 'ri-bank-line', label: 'Financial', detail: 'Vault & ATM' },
                { icon: 'ri-government-line', label: 'Government', detail: 'Multi-factor auth' },
                { icon: 'ri-truck-line', label: 'Logistics', detail: 'Cargo monitoring' },
                { icon: 'ri-restaurant-line', label: 'Food & Bev', detail: 'Health compliance' },
              ].map((item, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-5 text-center hover:bg-white/10 transition-all">
                  <div className="w-12 h-12 bg-[#D4A853]/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <i className={`${item.icon} text-2xl text-[#D4A853]`}></i>
                  </div>
                  <p className="text-white font-bold text-sm mb-1">{item.label}</p>
                  <p className="text-white/50 text-xs">{item.detail}</p>
                </div>
              ))}
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-7 sm:p-10">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
                <div className="md:col-span-2">
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">Need Compliance Documentation?</h3>
                  <p className="text-white/70 text-sm sm:text-base leading-relaxed">
                    We provide full system documentation, installation certificates, and monitoring records to satisfy insurance auditors, regulatory inspectors, and compliance officers. Our systems are designed to meet AGCO cannabis regulations, HIPAA security requirements, and municipal bylaw standards across the GTA.
                  </p>
                </div>
                <div className="text-center md:text-right">
                  <button
                    onClick={scrollToForm}
                    className="bg-[#D4A853] text-white px-7 py-3.5 rounded-full font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap"
                  >
                    Discuss Compliance Needs
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Multi-Location Section */}
        <section className="py-16 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                  ENTERPRISE SOLUTIONS
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-5">
                  Managing Multiple Business Locations?
                </h2>
                <p className="text-base sm:text-lg text-gray-600 mb-6 leading-relaxed">
                  Our enterprise security platform lets you monitor and manage all your locations from a single dashboard. View live feeds, review footage, manage access credentials, and receive alerts across every site — whether you have 2 locations or 20.
                </p>
                <div className="space-y-4 mb-8">
                  {[
                    { icon: 'ri-map-2-line', title: 'Centralized Dashboard', desc: 'One app to manage all your locations, cameras, and access points' },
                    { icon: 'ri-user-settings-line', title: 'Unified Access Management', desc: 'Add or remove employees across all sites with a single click' },
                    { icon: 'ri-bar-chart-line', title: 'Cross-Site Reporting', desc: 'Compare security incidents and activity across all your locations' },
                    { icon: 'ri-price-tag-3-line', title: 'Volume Pricing', desc: 'Significant discounts for businesses with 3 or more locations' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-11 h-11 bg-[#D4A853]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-xl text-[#D4A853]`}></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-[#0A1628] text-sm mb-0.5">{item.title}</h4>
                        <p className="text-sm text-gray-600">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <button onClick={scrollToForm} className="bg-[#D4A853] text-white px-8 py-3.5 rounded-full font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-lg">
                  Get Multi-Site Quote
                </button>
              </div>

              <div className="relative w-full h-[320px] sm:h-[480px] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20security%20monitoring%20dashboard%20on%20large%20screen%20showing%20multiple%20camera%20feeds%20from%20different%20business%20locations%20modern%20control%20room%20interface%20with%20map%20view%20and%20analytics%20data%20clean%20UI%20design&width=800&height=700&seq=bizmultisite1&orientation=portrait"
                  alt="Multi-Location Security Management"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/60 to-transparent"></div>
                <div className="absolute bottom-5 left-5 right-5">
                  <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-4">
                    <p className="text-white font-bold text-sm mb-1">Enterprise clients include:</p>
                    <p className="text-white/70 text-xs">Tim Hortons, Canadian Tire, Shoppers Drug Mart, Subway, and 500+ more</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-16 sm:py-24 bg-[#F8F9FA]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                OUR PROCESS
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-4">
                How We Secure Your Business
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
                A proven 4-step process from assessment to ongoing support
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {[
                { step: '01', icon: 'ri-search-eye-line', title: 'Free Site Assessment', desc: 'Our certified security consultant visits your business, identifies vulnerabilities, and maps optimal camera and sensor placement.' },
                { step: '02', icon: 'ri-draft-line', title: 'Custom System Design', desc: 'We design a tailored security solution for your specific industry, property layout, and budget — with transparent pricing.' },
                { step: '03', icon: 'ri-tools-line', title: 'Professional Installation', desc: 'Our licensed technicians install your system with minimal disruption. Most commercial installs are completed in 1–2 days.' },
                { step: '04', icon: 'ri-customer-service-2-line', title: 'Ongoing Support', desc: '24/7 monitoring, technical support, and system maintenance for the life of your system. We\'re always here when you need us.' },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-2xl p-7 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100 relative overflow-hidden">
                  <span className="absolute top-4 right-5 text-5xl font-extrabold text-[#D4A853]/10">{item.step}</span>
                  <div className="w-13 h-13 w-12 h-12 bg-[#D4A853] rounded-xl flex items-center justify-center mb-5">
                    <i className={`${item.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-[#0A1628] mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trusted Clients */}
        <section className="py-14 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                TRUSTED BY INDUSTRY LEADERS
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-3">
                Businesses That Trust Alliance Security
              </h2>
              <p className="text-base text-gray-600 max-w-xl mx-auto">
                From national chains to local businesses — protecting the GTA's most recognized brands
              </p>
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 sm:gap-4 mb-10">
              {[
                { name: 'Tim Hortons', img: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/a3f9260e0ccd0866f43363c524edfe05.png' },
                { name: 'Canadian Tire', img: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/e27adcf7cadbbcf092376d8388119615.jpeg' },
                { name: 'Shoppers Drug Mart', img: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/ff8f6ab6add3ac8f147a6768092ebd47.jpeg' },
                { name: 'Marriott Hotels', img: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/469dff91b229d0995c855e2a80feabff.png' },
                { name: 'Goodlife Fitness', img: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/69ed7aa3db7a11c53f836f7cd5216ede.png' },
                { name: 'Cineplex', img: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/68df4d95451fba1f20542cab9a0c42e9.png' },
                { name: 'LCBO', img: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/3a6547bcacca34d1321b942af4e3948a.png' },
                { name: 'Bulk Barn', img: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/8132d25968fe644fe51090f332b4195c.png' },
                { name: 'Esso', img: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/62b4690d857f54c9ea33e651269be273.png' },
                { name: 'Petro-Canada', img: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/6fd00cda4eae2accbb5b98dca0c1a8c2.png' },
                { name: 'Aramark', img: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/e3c7d0b317f44310ceb40367326e0199.png' },
                { name: 'Drake Hotel', img: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/6bba165c175149448f412e0b5a465dc8.jpeg' },
              ].map((client, i) => (
                <div key={i} className="bg-[#F8F9FA] rounded-xl p-3 sm:p-4 flex items-center justify-center h-20 sm:h-24 border border-gray-100 hover:border-[#D4A853]/30 hover:shadow-md transition-all">
                  <img src={client.img} alt={client.name} title={client.name} className="max-w-full max-h-full object-contain grayscale hover:grayscale-0 transition-all duration-300 scale-110" />
                </div>
              ))}
            </div>

            <div className="text-center">
              <Link to="/clients" className="inline-flex items-center gap-2 text-[#D4A853] font-semibold hover:gap-3 transition-all cursor-pointer">
                View All Our Clients <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-24 bg-[#F8F9FA]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-14">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                COMMON QUESTIONS
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-3">
                Business Security FAQs
              </h2>
              <p className="text-base text-gray-600">
                Answers to the most common questions from business owners across the GTA
              </p>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-white rounded-xl sm:rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-5 sm:p-6 text-left cursor-pointer"
                  >
                    <span className="font-semibold text-[#0A1628] pr-4 text-sm sm:text-base">{faq.question}</span>
                    <i className={`ri-arrow-down-s-line text-xl sm:text-2xl text-[#D4A853] transition-transform flex-shrink-0 ${openFaq === i ? 'rotate-180' : ''}`}></i>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 sm:px-6 pb-5 sm:pb-6">
                      <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-gray-600 text-sm mb-3">Have more questions?</p>
              <Link to="/faq" className="inline-flex items-center gap-2 text-[#D4A853] font-semibold hover:gap-3 transition-all cursor-pointer">
                Visit our full FAQ page <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Free Assessment Form */}
        <section id="business-assessment-form" className="py-16 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-sm font-semibold mb-5 whitespace-nowrap">
                  FREE BUSINESS ASSESSMENT
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-5 leading-tight">
                  Get a Free Commercial Security Assessment
                </h2>
                <p className="text-base sm:text-lg text-gray-600 mb-8 leading-relaxed">
                  Tell us about your business and our certified security consultants will design a custom solution — completely free, no obligation. We respond within 2 business hours.
                </p>

                <div className="space-y-5 mb-8">
                  {[
                    { icon: 'ri-timer-line', title: '2-Hour Response', desc: 'Our team responds to all inquiries within 2 business hours' },
                    { icon: 'ri-map-pin-line', title: 'Free On-Site Visit', desc: 'We come to your business for a thorough security assessment' },
                    { icon: 'ri-file-text-line', title: 'Detailed Proposal', desc: 'Itemized quote with no hidden fees or surprise charges' },
                    { icon: 'ri-shield-check-line', title: 'Zero Obligation', desc: 'The assessment and quote are completely free — no pressure' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-11 h-11 bg-[#D4A853]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-xl text-[#D4A853]`}></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-[#0A1628] text-sm mb-0.5">{item.title}</h4>
                        <p className="text-sm text-gray-500">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-4 p-5 bg-[#0A1628] rounded-2xl">
                  <div className="w-12 h-12 bg-[#D4A853] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-white"></i>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs mb-0.5">Prefer to speak with someone now?</p>
                    <a href="tel:18884589181" className="text-white font-bold text-lg hover:text-[#D4A853] transition-colors cursor-pointer">
                      1-888-458-9181
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-[#F8F9FA] rounded-3xl p-7 sm:p-10 border border-gray-200 shadow-lg">
                {formStatus === 'success' ? (
                  <div className="text-center py-14">
                    <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
                      <i className="ri-check-line text-4xl text-emerald-600"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-[#0A1628] mb-3">Assessment Request Received!</h3>
                    <p className="text-gray-600 mb-6 max-w-sm mx-auto text-sm">
                      Thank you! Our commercial security specialists will contact you within 2 business hours to schedule your free on-site assessment.
                    </p>
                    <button onClick={() => setFormStatus('idle')} className="text-[#D4A853] font-semibold hover:underline cursor-pointer whitespace-nowrap text-sm">
                      Submit another request
                    </button>
                  </div>
                ) : (
                  <>
                    <h3 className="text-xl font-bold text-[#0A1628] mb-1">Request Your Free Assessment</h3>
                    <p className="text-sm text-gray-500 mb-7">Fill out the form and we'll be in touch shortly.</p>

                    <form
                      id="business-security-form"
                      data-readdy-form
                      onSubmit={handleFormSubmit}
                      className="space-y-4"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-[#0A1628] mb-1.5">Full Name *</label>
                          <input type="text" name="fullName" required placeholder="John Smith" className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all" />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-[#0A1628] mb-1.5">Phone Number *</label>
                          <input type="tel" name="phone" required placeholder="(416) 555-0123" className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all" />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-[#0A1628] mb-1.5">Business Email *</label>
                        <input type="email" name="email" required placeholder="john@yourbusiness.com" className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all" />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-[#0A1628] mb-1.5">Business Name *</label>
                        <input type="text" name="businessName" required placeholder="Your Business Inc." className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all" />
                      </div>

                      <div className="relative">
                        <label className="block text-sm font-semibold text-[#0A1628] mb-1.5">Industry / Business Type *</label>
                        <input type="hidden" name="industry" value={selectedIndustry} />
                        <button
                          type="button"
                          onClick={() => setIndustryDropdownOpen(!industryDropdownOpen)}
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-[#D4A853] transition-all cursor-pointer"
                        >
                          <span className={selectedIndustry ? 'text-[#0A1628]' : 'text-gray-400'}>
                            {selectedIndustry || 'Select your industry'}
                          </span>
                          <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${industryDropdownOpen ? 'rotate-180' : ''}`}></i>
                        </button>
                        {industryDropdownOpen && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 max-h-56 overflow-y-auto">
                            {industryOptions.map((opt) => (
                              <button
                                key={opt}
                                type="button"
                                onClick={() => { setSelectedIndustry(opt); setIndustryDropdownOpen(false); }}
                                className={`w-full text-left px-4 py-3 text-sm hover:bg-[#D4A853]/10 transition-colors cursor-pointer ${selectedIndustry === opt ? 'text-[#D4A853] font-semibold bg-[#D4A853]/5' : 'text-gray-700'}`}
                              >
                                {opt}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-[#0A1628] mb-1.5">Number of Locations</label>
                          <input type="text" name="locations" placeholder="e.g. 1, 3, 10+" className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] transition-all" />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-[#0A1628] mb-1.5">City / Area</label>
                          <input type="text" name="city" placeholder="e.g. Mississauga, Toronto" className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] transition-all" />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-[#0A1628] mb-1.5">Security Needs & Details</label>
                        <textarea
                          name="details"
                          rows={3}
                          maxLength={500}
                          placeholder="Describe your current security setup, main concerns, and what you're looking for..."
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] transition-all resize-none"
                        ></textarea>
                        <p className="text-xs text-gray-400 mt-1">Max 500 characters</p>
                      </div>

                      {formStatus === 'error' && (
                        <div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-3 rounded-xl text-sm">
                          <i className="ri-error-warning-line text-lg"></i>
                          Something went wrong. Please try again or call us directly.
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={formStatus === 'submitting' || !selectedIndustry}
                        className="w-full bg-[#D4A853] text-white py-4 rounded-full text-base font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {formStatus === 'submitting' ? (
                          <><i className="ri-loader-4-line animate-spin text-lg"></i>Submitting...</>
                        ) : (
                          <>Request Free Business Assessment <i className="ri-arrow-right-line text-lg"></i></>
                        )}
                      </button>

                      <p className="text-xs text-gray-400 text-center">
                        By submitting, you agree to our <Link to="/privacy-policy" className="underline hover:text-[#D4A853] cursor-pointer">Privacy Policy</Link>. We never share your information.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-14 sm:py-20 bg-gradient-to-r from-[#D4A853] to-[#B8923D]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-2xl sm:text-4xl font-bold text-white mb-3">Ready to Secure Your Business?</h2>
            <p className="text-base sm:text-xl text-white/90 mb-7">
              Join 500+ GTA businesses that trust Alliance Security Systems. Free assessment, transparent pricing, no obligation.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <button
                onClick={scrollToForm}
                className="w-full sm:w-auto bg-white text-[#D4A853] px-8 sm:px-10 py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap shadow-xl"
              >
                Get Free Assessment
              </button>
              <a
                href="tel:18884589181"
                className="w-full sm:w-auto border-2 border-white text-white px-8 sm:px-10 py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-white hover:text-[#D4A853] transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-phone-line"></i>1-888-458-9181
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#050B14] text-white py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex flex-col leading-none">
                <span className="text-xl sm:text-2xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
                <span className="text-[8px] sm:text-[10px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
              </div>
            </Link>
            <div className="flex items-center gap-4 sm:gap-6 flex-wrap justify-center text-xs sm:text-sm">
              <Link to="/" className="text-white/60 hover:text-white transition-colors cursor-pointer">Home</Link>
              <Link to="/camera-packages" className="text-white/60 hover:text-white transition-colors cursor-pointer">Cameras</Link>
              <Link to="/alarm-packages" className="text-white/60 hover:text-white transition-colors cursor-pointer">Alarms</Link>
              <Link to="/access-control" className="text-white/60 hover:text-white transition-colors cursor-pointer">Access Control</Link>
              <Link to="/live-monitoring" className="text-white/60 hover:text-white transition-colors cursor-pointer">Live Monitoring</Link>
              <Link to="/clients" className="text-white/60 hover:text-white transition-colors cursor-pointer">Clients</Link>
              <Link to="/privacy-policy" className="text-white/60 hover:text-white transition-colors cursor-pointer">Privacy Policy</Link>
            </div>
            <p className="text-white/40 text-xs">© 2024 Alliance Security Systems. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
